import BigNumber from 'bignumber.js';
import { type Market, MarketType, type Prediction } from '@/common/predictionMarkets/types.ts';
import { BASIS_POINTS_DIVISOR, MAX_SETTLEMENT_CACHE_SIZE, MAX_SETTLEMENT_CONTEXT_CACHE_SIZE } from '@/common/config';
import { logDEBUG } from '@/common/debug';

type OutcomeId = string;
type UserId = string;

type Wager = {
  userId: UserId;
  outcomeId: OutcomeId;
  amount: BigNumber; // stake
  shares: bigint; // aggregated shares for live (0 for deadline ok) - using native bigint for integer-only operations
  // optional: keep original prediction id if you want
};

export interface UserOutcomeResult {
  userId: string;
  outcome: string;

  predictionStake: string;

  volumeShare: string;
  stakeAmountUsed: string;
  winAmount: string;
  refundAfterWin: string;
  feeAmount: string;
  feeWei?: bigint; // Internal: raw wei value for accurate accumulation

  potentialLoss: string;
  lossShare: string;
  realLoss: string;
  refundAfterLoss: string;

  shares: string;

  profit: string;
  loss: string;

  totalPayout: string;
}

export interface UserTotalsResult {
  userId: string;
  totalStake: BigNumber;
  totalWinAmount: BigNumber;
  totalLossAmount: BigNumber;
  totalRefund: BigNumber;
  totalPayout: BigNumber;
}

export interface MarketSettlementResult {
  totalsPerOutcome: Record<string, BigNumber>;
  wantedPayoutPerOutcome: Record<string, BigNumber>;
  smallestWantedPayout: BigNumber;
  maxStakePerOutcome: Record<string, BigNumber>;

  totalSharesPerOutcome?: Record<string, bigint>; // Using native bigint for integer-only share tracking

  totalWinAmount: BigNumber;
  totalLoserMaxLoss: BigNumber;

  userOutcomeResults: Record<string, Record<string, UserOutcomeResult>>;
  userTotals: Record<string, UserTotalsResult>;

  // Memoized aggregations to avoid nested loops in components
  totalFees: number; // Sum of all feeAmount across all users and outcomes
}

/** ---------- Common helpers ---------- */

function bn0() {
  return new BigNumber(0);
}

// ========================================
// Market Context Cache
// ========================================
// Cache buildContext() results to avoid redundant aggregations when multiple
// PredictionResultRow components render for the same market
// Once a market is settled (winningOutcomeId set), context is IMMUTABLE and can be cached forever
const marketContextCache = new Map<string, AnyCtx>();

function getMarketContextCacheKey(market: Market): string {
  // For SETTLED markets: context is immutable, cache forever with just market ID + winner
  // For UNSETTLED markets: include totalPredictionsCount to invalidate when predictions change
  if (market.winningOutcomeId) {
    return `${market.id}:settled:${market.winningOutcomeId}`;
  }
  return `${market.id}:active:${market.totalPredictionsCount}`;
}

function getCachedContext(market: Market): AnyCtx | null {
  const key = getMarketContextCacheKey(market);
  const cached = marketContextCache.get(key);

  if (cached) {
    // LRU: Move to end by deleting and re-inserting
    marketContextCache.delete(key);
    marketContextCache.set(key, cached);
    return cached;
  }

  return null;
}

function cacheContext(market: Market, ctx: AnyCtx): void {
  // LRU eviction if cache is full
  if (marketContextCache.size >= MAX_SETTLEMENT_CONTEXT_CACHE_SIZE) {
    const oldestKey = marketContextCache.keys().next().value as string;
    marketContextCache.delete(oldestKey);
  }

  const key = getMarketContextCacheKey(market);
  marketContextCache.set(key, ctx);
}

function feeRate(market: Market): BigNumber {
  return new BigNumber(market.feeBps.toString()).dividedBy(BASIS_POINTS_DIVISOR);
}

function baseOutcomeResult(w: Wager): UserOutcomeResult {
  return {
    userId: w.userId,
    outcome: w.outcomeId,
    predictionStake: w.amount.toFixed(2),

    volumeShare: '0',
    stakeAmountUsed: '0',
    winAmount: '0',
    refundAfterWin: '0',
    feeAmount: '0',

    potentialLoss: '0',
    lossShare: '0',
    realLoss: '0',
    refundAfterLoss: '0',

    shares: w.shares.toString(),

    profit: '0',
    loss: '0',
    totalPayout: '0',
  };
}

function outcomeProbability(market: Market, outcomeId: string): BigNumber {
  const o = market.outcomes.find((x) => x.id === outcomeId);
  if (!o) throw new Error(`Outcome not found: ${outcomeId}`);
  return new BigNumber(o.probability).dividedBy(100);
}

function losingProbSum(market: Market, winningOutcomeId: string): BigNumber {
  let sum = bn0();
  for (const o of market.outcomes) {
    if (o.id !== winningOutcomeId) {
      sum = sum.plus(new BigNumber(o.probability).dividedBy(100));
    }
  }
  return sum;
}

function isNonZeroLike(v: string) {
  return v !== '0' && v !== '0.00' && v !== '0.0000';
}

/** ---------- Aggregation / context ---------- */

type SettlementContextBase = {
  market: Market;
  winningOutcomeId: string;
  totalsPerOutcome: Record<string, BigNumber>;
  totalSharesPerOutcome: Record<string, bigint>; // Using native bigint for integer-only share tracking
  wantedPayoutPerOutcome: Record<string, BigNumber>;
  smallestWantedPayout: BigNumber;
  maxStakePerOutcome: Record<string, BigNumber>;
  totalWinAmount: BigNumber;
  totalLoserMaxLoss: BigNumber;
  losingOutcomesTotalProbability: BigNumber;
};

type DeadlineCtx = SettlementContextBase & { marketType: MarketType.Deadline };
type LiveCtx = SettlementContextBase & {
  marketType: MarketType.Live;
  totalVolume: BigNumber; // normal decimals
  totalVolumeWei: bigint; // Using native bigint for integer wei calculations
};

type AnyCtx = DeadlineCtx | LiveCtx;

/**
 * Get or build market context with caching
 * Avoids redundant aggregations when multiple components render for same market
 */
function getOrBuildContext(market: Market): AnyCtx {
  // Check cache first
  const cached = getCachedContext(market);
  if (cached) {
    return cached;
  }

  // Build and cache
  const ctx = buildContext(market);
  cacheContext(market, ctx);
  return ctx;
}

/**
 * Build market context (aggregates all predictions)
 * This is expensive - use getOrBuildContext() instead to leverage caching
 */
function buildContext(market: Market): AnyCtx {
  if (!market.winningOutcomeId) {
    throw new Error('Winning outcome ID is required for settlement calculation');
  }

  const winningOutcomeId = market.winningOutcomeId;

  // Use pre-computed totals from contract instead of aggregating predictions
  const totalsPerOutcome: Record<string, BigNumber> = {};
  const totalSharesPerOutcome: Record<string, bigint> = {};
  let totalVolume = bn0();

  for (const [outcomeId, volume] of Object.entries(market.totalsPerOutcome)) {
    const volumeBN = new BigNumber(volume);
    totalsPerOutcome[outcomeId] = volumeBN;
    totalVolume = totalVolume.plus(volumeBN);
  }

  for (const [outcomeId, shares] of Object.entries(market.totalSharesPerOutcome)) {
    totalSharesPerOutcome[outcomeId] = shares;
  }

  // wanted payout + maxStakePerOutcome (used by Deadline rules; harmless for Live)
  const wantedPayoutPerOutcome: Record<string, BigNumber> = {};
  for (const o of market.outcomes) {
    const prob = new BigNumber(o.probability).dividedBy(100);
    const vol = totalsPerOutcome[o.id] ?? bn0();
    wantedPayoutPerOutcome[o.id] = prob.isZero() ? bn0() : vol.dividedBy(prob);
  }

  const wantedValues = Object.values(wantedPayoutPerOutcome);
  const smallestWantedPayout = wantedValues.length ? BigNumber.min(...wantedValues) : bn0();

  const maxStakePerOutcome: Record<string, BigNumber> = {};
  for (const o of market.outcomes) {
    const prob = new BigNumber(o.probability).dividedBy(100);
    maxStakePerOutcome[o.id] = smallestWantedPayout.multipliedBy(prob);
  }

  const winProb = outcomeProbability(market, winningOutcomeId);
  const losingSum = losingProbSum(market, winningOutcomeId);

  const maxStakeForWinningOutcome = maxStakePerOutcome[winningOutcomeId] ?? bn0();
  const totalWinAmount = winProb.isZero() ? bn0() : maxStakeForWinningOutcome.dividedBy(winProb);
  const totalLoserMaxLoss = totalWinAmount.times(losingSum);

  if (market.marketType === MarketType.Live) {
    // Convert to wei using native bigint for integer-only operations (much faster than BigNumber)
    // Use string conversion to avoid precision loss with large numbers (toNumber() can lose precision)
    const totalVolumeWei = BigInt(totalVolume.multipliedBy(1e18).toFixed(0));
    return {
      marketType: MarketType.Live,
      market,
      winningOutcomeId,
      totalsPerOutcome,
      totalSharesPerOutcome,
      wantedPayoutPerOutcome,
      smallestWantedPayout,
      maxStakePerOutcome,
      totalWinAmount,
      totalLoserMaxLoss,
      losingOutcomesTotalProbability: losingSum,
      totalVolume,
      totalVolumeWei,
    };
  }

  if (market.marketType === MarketType.Deadline) {
    return {
      marketType: MarketType.Deadline,
      market,
      winningOutcomeId,
      totalsPerOutcome,
      totalSharesPerOutcome,
      wantedPayoutPerOutcome,
      smallestWantedPayout,
      maxStakePerOutcome,
      totalWinAmount,
      totalLoserMaxLoss,
      losingOutcomesTotalProbability: losingSum,
    };
  }

  throw new Error(`Unsupported marketType: ${market.marketType}`);
}

/** ---------- Rules per market type ---------- */

type SettleArgs = {
  ctx: AnyCtx;
  wager: Wager;
  // if you keep play logic: decide at call-site and use this flag.
  isPlay?: boolean;
};

function settleWager({ ctx, wager, isPlay }: SettleArgs): UserOutcomeResult {
  const market = ctx.market;

  // Void: refund full stake for everyone (simple + centralized)
  if (market.isVoid) {
    const r = baseOutcomeResult(wager);
    return {
      ...r,
      stakeAmountUsed: '0',
      refundAfterLoss: wager.amount.toFixed(2),
      totalPayout: wager.amount.toFixed(2),
      realLoss: '0',
      loss: '0',
    };
  }

  // No winning outcome => nothing to settle
  if (!ctx.winningOutcomeId) {
    return baseOutcomeResult(wager);
  }

  const isWinner = wager.outcomeId === ctx.winningOutcomeId;

  // Play-account emulation: you currently emulate as “deadline win calc” and “lose full stake”
  // Keep it here so both individual + totals share the same behavior.
  if (isPlay) {
    if (!isWinner) {
      const r = baseOutcomeResult(wager);
      return {
        ...r,
        stakeAmountUsed: wager.amount.toFixed(2),
        realLoss: wager.amount.toFixed(2),
        potentialLoss: wager.amount.toFixed(2),
        totalPayout: '0',
        loss: wager.amount.toFixed(2),
      };
    }

    const prob = outcomeProbability(market, wager.outcomeId);
    const fr = feeRate(market);
    const winBeforeFee = prob.isZero() ? bn0() : wager.amount.dividedBy(prob);
    const fee = winBeforeFee.multipliedBy(fr);
    const win = winBeforeFee.minus(fee);
    const feeWei = BigInt(Math.floor(fee.toNumber() * 1e18));

    const r = baseOutcomeResult(wager);
    return {
      ...r,
      stakeAmountUsed: wager.amount.toFixed(2),
      winAmount: win.toFixed(2),
      feeAmount: fee.toFixed(2),
      feeWei,
      totalPayout: win.toFixed(2),
      profit: win.minus(wager.amount).toFixed(2),
    };
  }

  // Shared fields
  const totalOnOutcome = ctx.totalsPerOutcome[wager.outcomeId] ?? bn0();
  const volumeShare = totalOnOutcome.isZero() ? bn0() : wager.amount.dividedBy(totalOnOutcome);

  if (ctx.marketType === MarketType.Live) {
    // LIVE rules:
    // - winners: payout = (totalPool * shares / totalSharesOnWinningOutcome) - fee
    // - losers: lose full stake
    if (!isWinner) {
      const r = baseOutcomeResult(wager);
      return {
        ...r,
        volumeShare: volumeShare.toFixed(4),
        stakeAmountUsed: wager.amount.toFixed(2),
        potentialLoss: wager.amount.toFixed(2),
        realLoss: wager.amount.toFixed(2),
        refundAfterLoss: '0',
        totalPayout: '0',
        loss: wager.amount.toFixed(2),
      };
    }

    const outcomeTotalShares = ctx.totalSharesPerOutcome[wager.outcomeId] ?? 0n;
    if (outcomeTotalShares === 0n) {
      // if this happens it's a data invariant problem
      return baseOutcomeResult(wager);
    }

    // Use native bigint for integer division (much faster than BigNumber)
    const winBeforeFeeWei = (ctx.totalVolumeWei * wager.shares) / outcomeTotalShares;
    // Keep fee calculation in bigint space to avoid precision loss
    const feeBps = BigInt(market.feeBps.toString());
    const feeWei = (winBeforeFeeWei * feeBps) / 10000n; // Divide by 10000 for basis points
    const winWei = winBeforeFeeWei - feeWei;

    // Convert back to decimal for display
    const win = new BigNumber(winWei.toString()).shiftedBy(-18);
    const fee = new BigNumber(feeWei.toString()).shiftedBy(-18);

    const r = baseOutcomeResult(wager);
    return {
      ...r,
      volumeShare: volumeShare.toFixed(4),
      stakeAmountUsed: wager.amount.toFixed(2),
      winAmount: win.toFixed(2),
      refundAfterWin: '0',
      feeAmount: fee.toFixed(2),
      feeWei, // Store raw wei for accumulation without rounding errors
      totalPayout: win.toFixed(2),
      profit: win.minus(wager.amount).toFixed(2),
      shares: wager.shares.toString(),
    };
  }

  // DEADLINE rules:
  // - winners: capped stake usage (maxStakePerOutcome * volumeShare), refund unused, fee on win
  // - losers: realLoss = totalLoserMaxLoss * (prob/losingSum) * volumeShare, refund rest
  if (isWinner) {
    const prob = outcomeProbability(market, wager.outcomeId);
    const maxStake = ctx.maxStakePerOutcome[wager.outcomeId] ?? bn0();
    const maxStakeForUser = maxStake.multipliedBy(volumeShare);
    const stakeUsed = BigNumber.min(wager.amount, maxStakeForUser);

    const winBeforeFee = prob.isZero() ? bn0() : stakeUsed.dividedBy(prob);
    const fee = winBeforeFee.multipliedBy(feeRate(market));
    const win = winBeforeFee.minus(fee);
    const feeWei = BigInt(Math.floor(fee.toNumber() * 1e18));

    const refund = wager.amount.minus(stakeUsed);
    const totalPayout = win.plus(refund);

    const r = baseOutcomeResult(wager);
    return {
      ...r,
      volumeShare: volumeShare.toFixed(4),
      stakeAmountUsed: stakeUsed.toFixed(2),
      winAmount: win.toFixed(2),
      refundAfterWin: refund.toFixed(2),
      feeAmount: fee.toFixed(2),
      feeWei,
      totalPayout: totalPayout.toFixed(2),
      profit: totalPayout.minus(wager.amount).toFixed(2),
      shares: '0',
    };
  } else {
    const prob = outcomeProbability(market, wager.outcomeId);
    const losingSum = ctx.losingOutcomesTotalProbability;

    const outcomeLossShare = losingSum.isZero() ? bn0() : prob.dividedBy(losingSum);
    const userLossShare = outcomeLossShare.multipliedBy(volumeShare);

    const realLoss = ctx.totalLoserMaxLoss.multipliedBy(userLossShare);
    const refund = wager.amount.minus(realLoss);

    const r = baseOutcomeResult(wager);
    return {
      ...r,
      volumeShare: volumeShare.toFixed(4),
      stakeAmountUsed: wager.amount.toFixed(2),
      potentialLoss: wager.amount.toFixed(2),
      lossShare: volumeShare.toFixed(4),
      realLoss: realLoss.toFixed(2),
      refundAfterLoss: refund.toFixed(2),
      totalPayout: refund.toFixed(2),
      loss: realLoss.toFixed(2),
      shares: '0',
    };
  }
}

/** ---------- Public API: individual prediction ---------- */

function getIndividualSettlementCacheKey(market: Market, prediction: Prediction): string {
  // Create hash from data that affects individual settlement calculation
  const isPlay = prediction.id.endsWith('-play');
  return `${market.id}:${prediction.id}:${market.isSettled}:${market.isVoid}:${market.winningOutcomeId || 'none'}:${isPlay}`;
}

export function calculateIndividualPredictionSettlement(market: Market, prediction: Prediction): UserOutcomeResult {
  // Check cache first (settlements are immutable, pure LRU eviction)
  const cacheKey = getIndividualSettlementCacheKey(market, prediction);
  const cached = individualSettlementCache.get(cacheKey);

  if (cached) {
    // LRU: Move to end by deleting and re-inserting
    individualSettlementCache.delete(cacheKey);
    individualSettlementCache.set(cacheKey, cached);
    return cached.result;
  }

  // Winner/loser is handled inside settleWager; we only need a context for non-play deadline caps etc.
  // But you can skip building context for play if you want.
  const isPlay = prediction.id.endsWith('-play');

  const wager: Wager = {
    userId: prediction.userId,
    outcomeId: prediction.outcomeId,
    amount: new BigNumber(prediction.amount),
    shares: BigInt(prediction.shares ?? 0),
  };

  // Handle void markets first - simple full refund without needing context
  if (market.isVoid) {
    const r = baseOutcomeResult(wager);
    return {
      ...r,
      stakeAmountUsed: '0',
      refundAfterLoss: wager.amount.toFixed(2),
      totalPayout: wager.amount.toFixed(2),
      realLoss: '0',
      loss: '0',
    };
  }

  const ctx = market.winningOutcomeId ? getOrBuildContext(market) : null;
  const result = !market.winningOutcomeId || !ctx ? baseOutcomeResult(wager) : settleWager({ ctx, wager, isPlay });

  // Cache the result with LRU eviction
  // If cache is at max size, delete oldest entry before adding new one
  if (individualSettlementCache.size >= MAX_SETTLEMENT_CACHE_SIZE) {
    const oldestKey = individualSettlementCache.keys().next().value as string;
    individualSettlementCache.delete(oldestKey);
  }

  individualSettlementCache.set(cacheKey, {
    result,
    timestamp: Date.now(),
    dataHash: cacheKey,
  });

  return result;
}

/** ---------- Public API: full market settlement ---------- */

// Module-level cache for settlement calculations (persists across component lifecycles)
interface SettlementCacheEntry {
  result: MarketSettlementResult;
  timestamp: number;
  // Hash of relevant market data to detect when recalculation needed
  dataHash: string;
}

const settlementCache = new Map<string, SettlementCacheEntry>();

// Module-level cache for individual prediction settlements (persists across component lifecycles)
interface IndividualSettlementCacheEntry {
  result: UserOutcomeResult;
  timestamp: number;
  dataHash: string;
}

const individualSettlementCache = new Map<string, IndividualSettlementCacheEntry>();

/**
 * Clear both settlement calculation caches (rarely needed since settlements are immutable)
 * Caches use LRU eviction (MAX_SETTLEMENT_CACHE_SIZE) and never expire by time
 * Exported for manual cache clearing operations (e.g., account switch)
 */
export function clearSettlementCache() {
  logDEBUG('[CACHE CLEAR] Settlement calculation cache (all markets + individual predictions)');
  settlementCache.clear();
  individualSettlementCache.clear();
}

function getSettlementCacheKey(market: Market): string {
  // Create hash from data that affects settlement calculation
  // Use totalPredictionsCount from contract (more efficient than manual counting)
  return `${market.id}:${market.isSettled}:${market.isVoid}:${market.winningOutcomeId || 'none'}:${market.totalPredictionsCount}`;
}

export function calculateMarketSettlement(market: Market): MarketSettlementResult {
  // Check cache first (settlements are immutable, pure LRU eviction)
  const cacheKey = getSettlementCacheKey(market);
  const cached = settlementCache.get(cacheKey);

  if (cached) {
    // LRU: Move to end by deleting and re-inserting
    settlementCache.delete(cacheKey);
    settlementCache.set(cacheKey, cached);
    return cached.result;
  }
  if (!market.winningOutcomeId) {
    return {
      totalsPerOutcome: {},
      wantedPayoutPerOutcome: {},
      smallestWantedPayout: bn0(),
      maxStakePerOutcome: {},
      totalSharesPerOutcome: {},
      totalWinAmount: bn0(),
      totalLoserMaxLoss: bn0(),
      userOutcomeResults: {},
      userTotals: {},
      totalFees: 0,
    };
  }

  const ctx = getOrBuildContext(market);

  // Group predictions into wagers per user/outcome
  // Each prediction is one transaction; we aggregate by user+outcome for settlement
  const userOutcomeWagers: Record<string, Record<string, Wager>> = {};
  for (const [userId, preds] of Object.entries(market.userIdToPredictions)) {
    for (const p of preds) {
      const outcomeId = p.outcomeId;
      userOutcomeWagers[userId] ??= {};
      userOutcomeWagers[userId][outcomeId] ??= {
        userId,
        outcomeId,
        amount: bn0(),
        shares: 0n,
      };
      userOutcomeWagers[userId][outcomeId].amount = userOutcomeWagers[userId][outcomeId].amount.plus(p.amount);
      userOutcomeWagers[userId][outcomeId].shares = userOutcomeWagers[userId][outcomeId].shares + p.shares;
    }
  }

  const userOutcomeResults: Record<string, Record<string, UserOutcomeResult>> = {};
  const userTotals: Record<string, UserTotalsResult> = {};
  let totalFeesWei = 0n; // Accumulate in wei to avoid rounding errors

  for (const [userId, outcomes] of Object.entries(userOutcomeWagers)) {
    userOutcomeResults[userId] = {};
    userTotals[userId] = {
      userId,
      totalStake: bn0(),
      totalWinAmount: bn0(),
      totalLossAmount: bn0(),
      totalRefund: bn0(),
      totalPayout: bn0(),
    };

    for (const [outcomeId, wager] of Object.entries(outcomes)) {
      const isPlay = false;

      const r = settleWager({ ctx, wager, isPlay });
      userOutcomeResults[userId][outcomeId] = r;

      const stake = wager.amount;
      userTotals[userId].totalStake = userTotals[userId].totalStake.plus(stake);

      // Parse amounts back to BigNumber for totals (keep internally BN to avoid float drift)
      const win = new BigNumber(r.winAmount);
      const loss = new BigNumber(r.realLoss);
      const refund = new BigNumber(isNonZeroLike(r.refundAfterWin) ? r.refundAfterWin : r.refundAfterLoss);
      const payout = new BigNumber(r.totalPayout);

      userTotals[userId].totalWinAmount = userTotals[userId].totalWinAmount.plus(win);
      userTotals[userId].totalLossAmount = userTotals[userId].totalLossAmount.plus(loss);
      userTotals[userId].totalRefund = userTotals[userId].totalRefund.plus(refund);
      userTotals[userId].totalPayout = userTotals[userId].totalPayout.plus(payout);

      // Accumulate fees only for unsettled markets (preview/simulation)
      // For settled markets, use pre-calculated value from contract
      if (!market.isSettled && r.feeWei) {
        totalFeesWei += r.feeWei;
      }
    }
  }

  // Use contract's pre-calculated creator fees for settled markets (much faster than looping)
  // For unsettled markets (preview), calculate from accumulated fees
  // Note: market.creatorFeesTotal is the creator's portion (50%), so multiply by 2 for total fees
  const totalFees =
    market.isSettled && market.creatorFeesTotal > 0
      ? market.creatorFeesTotal * 2
      : Number(new BigNumber(totalFeesWei.toString()).shiftedBy(-18).toFixed(2));

  const result: MarketSettlementResult = {
    totalsPerOutcome: ctx.totalsPerOutcome,
    wantedPayoutPerOutcome: ctx.wantedPayoutPerOutcome,
    smallestWantedPayout: ctx.smallestWantedPayout,
    maxStakePerOutcome: ctx.maxStakePerOutcome,
    totalSharesPerOutcome: ctx.totalSharesPerOutcome,
    totalWinAmount: ctx.totalWinAmount,
    totalLoserMaxLoss: ctx.totalLoserMaxLoss,
    userOutcomeResults,
    userTotals,
    totalFees, // Accumulated in wei, converted once at end
  };

  // Cache the result with LRU eviction
  // If cache is at max size, delete oldest entry before adding new one
  if (settlementCache.size >= MAX_SETTLEMENT_CACHE_SIZE) {
    const oldestKey = settlementCache.keys().next().value as string;
    settlementCache.delete(oldestKey);
  }

  settlementCache.set(cacheKey, {
    result,
    timestamp: Date.now(),
    dataHash: cacheKey,
  });

  return result;
}
